import React from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function CredibilitySection() {
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation({
    threshold: 0.1,
  });
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation({
    threshold: 0.1,
  });

  /** 
   * Opens the capability statement PDF in a new tab.
   * If the link is missing or fails, we log an error instead of breaking the UI.
   */
  const handleDownload = () => {
    const url = 'https://drive.google.com/file/d/1RtP25D3JwWYrVXhe-1US4mOCwWdJmBft/view?usp=drive_link';
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Failed to open PDF:', err);
    }
  };

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden bg-[#1A0A02]">
      {/* Decorative background pattern */}
      <div className="absolute inset-0 opacity-10" aria-hidden="true">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="credibility-dots" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
              <circle cx="20" cy="20" r="1.5" fill="#E8C580" />
            </pattern>
            <pattern id="credibility-circles" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
              <circle cx="60" cy="60" r="40" fill="none" stroke="#BA3D03" strokeWidth="0.5" />
              <circle cx="60" cy="60" r="20" fill="none" stroke="#E8C580" strokeWidth="0.3" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#credibility-dots)" />
          <rect width="100%" height="100%" fill="url(#credibility-circles)" />
        </svg>
      </div>

      {/* Warm glow accents */}
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-[#BA3D03]/10 blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true" />
      <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-[#E8C580]/8 blur-3xl translate-x-1/3 translate-y-1/3 pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-5 gap-12 items-center">
          {/* Left content */}
          <div
            ref={leftRef as React.RefObject<HTMLDivElement>}
            className={`lg:col-span-3 transition-all duration-700 ease-out ${
              leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <span className="text-xs uppercase tracking-widest text-[#E8C580] font-semibold">
              DELIVERED BY
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mt-4 mb-4">
              INDIGENOUS ENGAGEMENT AND FACILITATION PTY LTD
            </h2>

            {/* Intro sentence */}
            <p className="text-lg text-[#F5DDB8] leading-relaxed mb-6 max-w-xl">
              Specialists in Country-centred practice for NSW built environment professionals.
            </p>

            {/* Bullet proof points */}
            <ul className="space-y-4 mb-6 max-w-xl">
              {[
                {
                  icon: 'ri-award-line',
                  text: 'Training that moves beyond compliance into genuine, meaningful cultural integration.',
                },
                {
                  icon: 'ri-map-pin-2-line',
                  text: 'Hands-on experience with Traditional Custodians, agencies and project teams across real planning and infrastructure work.',
                },
                {
                  icon: 'ri-shield-check-line',
                  text: 'Cultural safety within all aspects of Community Engagement | Facilitated sessions designed to create physical and psychologically safe learning environments, where practitioners supported to ask challenging questions and build genuine capability.',
                },
                {
                  icon: 'ri-tools-line',
                  text: 'Practice over tokenism | our methodology is grounded in genuine project delivery | Teams leave with tools they can apply immediately in active projects.',
                },
                {
                  icon: 'ri-book-open-line',
                  text: 'All training aligned with the Better Placed framework and Connecting with Country principles.',
                },
              ].map(({ icon, text }) => (
                <li key={icon} className="grid grid-cols-[24px_1fr] gap-3 items-start">
                  <span className="w-6 h-6 flex items-center justify-center mt-0.5 shrink-0">
                    <i className={`${icon} text-[#E8C580] text-lg`} aria-hidden="true"></i>
                  </span>
                  <span className="text-base text-[#F5DDB8] leading-relaxed">{text}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={handleDownload}
              className="w-full sm:w-auto px-8 py-4 bg-[#E8C580] text-[#1A0A02] rounded-lg font-semibold hover:bg-[#F5DDB8] transition-all shadow-lg hover:shadow-xl flex items-center justify-center sm:justify-start gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-file-pdf-line text-xl" aria-hidden="true"></i>
              Download Capability Statement
            </button>
          </div>

          {/* Right image */}
          <div
            ref={rightRef as React.RefObject<HTMLDivElement>}
            className={`lg:col-span-2 transition-all duration-700 ease-out delay-200 ${
              rightVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <img
              src="https://static.readdy.ai/image/597b2346b2ff492b6c1bd141556b2fdd/47879b068ce831d88ebc5494adf4ad6a.jpeg"
              alt="Course facilitator"
              className="w-full h-auto rounded-2xl border-2 border-[#E8C580]/40 shadow-2xl object-cover"
            />
            {/* Photo caption */}
            <div className="mt-3 text-center">
              <p className="text-sm font-semibold text-white">CHARLES TRINDALL</p>
              <p className="text-xs text-[#D4A96A] mt-0.5">Lead Facilitator | Country-centred Practice Specialist</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
